import { createApi } from "@reduxjs/toolkit/query/react";
import baseQuery from "../baseQuery";
import {
  COMPARE_PRODUCTS_URL,
  COUNTRIES_STATES_URL,
  DEVICES_URL,
  PLAN_URL,
  PRODUCTS_URL,
  SIMILAR_PRODUCTS_URL,
  API_PROMO_CODE_URL,
  BASE_URL,
  API_LOGIN_URL,
  API_CART_URL,
  GET_SHIPPING_METHODS_URL,
  PAY_NOW,
  LOGIN_CHECKOUT_URL,
  UPDATE_PRODUCT,
} from "@/utils/api/Urls";

export const marketing = createApi({
  reducerPath: "marketing",
  baseQuery,
  tagTypes: [
    "Devices",
    "Countries",
    "Products",
    "Plans",
    "Similar",
    "Comparison",
    "Promo",
    "Cart",
    "Shipping",
    "Pay",
  ],
  endpoints: (builder) => ({
    // Get all devices
    getDevices: builder.query({
      query: () => `${DEVICES_URL}`,
      transformResponse: (response: { products: IDevice[] }) =>
        response.products,
      providesTags: ["Devices"],
    }),

    // Get countries and states
    getCountriesAndStates: builder.query({
      query: () => `${COUNTRIES_STATES_URL}`,
      transformResponse: (response: {
        countries: ICountry[];
        states: IState[];
      }) => response,
      providesTags: ["Countries"],
    }),

    // Get products
    getProducts: builder.query({
      query: () => `${PRODUCTS_URL}`,
      transformResponse: (response: { products: IProduct[] }) =>
        response.products,
      providesTags: ["Products"],
    }),

    // Get subscription plan based on chosen country and device
    getPlans: builder.query({
      query: ({ countryId = 1, deviceId }) =>
        deviceId !== 0
          ? `${PLAN_URL}?CountryId=${countryId}&DeviceId=${deviceId}`
          : `${PLAN_URL}?CountryId=${countryId}`,

      transformResponse: (response: IPlanResponse) => response,
      providesTags: ["Plans"],
    }),

    // Get similar products
    getSimilarProducts: builder.query({
      query: () => `${SIMILAR_PRODUCTS_URL}`,
      transformResponse: (response: ISimilarProduct[]) => response,
      providesTags: ["Similar"],
    }),

    // Get device comparison
    getProductComparison: builder.query({
      query: () => `${COMPARE_PRODUCTS_URL}`,
      transformResponse: (response: IComparisonResponse) => response,
      providesTags: ["Comparison"],
    }),

    // Apply promo code
    applyPromoCode: builder.mutation({
      query: (promoCode) => ({
        url: `${BASE_URL}${API_PROMO_CODE_URL}/${promoCode}`,
        method: "GET",
      }),
      transformResponse: (response: { id: null; percentageOff: number }) =>
        response,
      invalidatesTags: ["Promo"],
    }),

    // Login
    login: builder.mutation({
      query: (data) => ({
        url: `${LOGIN_CHECKOUT_URL}${API_LOGIN_URL}`,
        method: "POST",
        body: data,
        credentials: "include",
      }),
    }),

    // Add or edit cart
    addEditCart: builder.mutation({
      query: (productData) => ({
        url: `${API_CART_URL}/item`,
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: productData,
      }),
      invalidatesTags: ["Cart"],
    }),

    // Pay now
    addPayNow: builder.mutation({
      query: (productData) => {
        const { captcha, ...restData } = productData;
        const headers = {
          "Content-Type": "application/json",
          "Captcha-Input": captcha,
        };
        return {
          url: `${PAY_NOW}/pay-now`,
          method: "POST",
          headers,
          body: restData,
        };
      },
      invalidatesTags: ["Pay"],
    }),

    // Get shipping methods
    getShipping: builder.query({
      query: () => `${GET_SHIPPING_METHODS_URL}`,
      transformResponse: (response: { shipping: IShipping }) => response,
      providesTags: ["Shipping"],
    }),

    // Update product
    upadateProduct: builder.mutation({
      query: (productData) => ({
        url: `${UPDATE_PRODUCT}`,
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: productData,
      }),
      invalidatesTags: ["Cart"],
    }),
  }),
});

export const {
  useGetDevicesQuery,
  useGetCountriesAndStatesQuery,
  useGetProductsQuery,
  useGetPlansQuery,
  useUpadateProductMutation,
  useGetSimilarProductsQuery,
  useGetProductComparisonQuery,
  useApplyPromoCodeMutation,
  useLoginMutation,
  useAddEditCartMutation,
  useAddPayNowMutation,
  useGetShippingQuery,
} = marketing;
